import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import './App.css';
import './edit.css';
import './auth.css';
import './empty.css';
import './burn.css';
import './vault.css';
import useSections from './hooks/useSections';
import useExpenses from './hooks/useExpenses';
import DashboardSection from './components/DashboardSection';
import SortableDashboard from './components/SortableDashboard';
import QuickLaunch from './components/QuickLaunch';
import QuickLaunchPickerDialog from './components/QuickLaunchPickerDialog';
import SectionNameDialog from './components/SectionNameDialog';
import ConfirmDeleteDialog from './components/ConfirmDeleteDialog';
import AppDialog from './components/AppDialog';
import MoveAppDialog from './components/MoveAppDialog';
import ConfirmAppDeleteDialog from './components/ConfirmAppDeleteDialog';
import LoginScreen from './components/LoginScreen';
import AccountMenu from './components/AccountMenu';
import WorkspaceState from './components/WorkspaceState';
import Segmented from './components/Segmented';
import BurnRateView from './components/BurnRateView';
import VaultView from './components/VaultView';
import VaultToolDialog from './components/VaultToolDialog';
import ConfirmVaultMoveDialog from './components/ConfirmVaultMoveDialog';
import MoveVaultToolDialog from './components/MoveVaultToolDialog';
import DeleteVaultToolDialog from './components/DeleteVaultToolDialog';

const VIEWS = [
  { id: 'apps', label: 'Apps' },
  { id: 'burn', label: 'Burn' }
];

function App() {
  const {
    session,
    authReady,
    signIn,
    signOut,
    workspace,
    sections,
    allApps,
    vaultApps,
    quickLaunchIds,
    quickLaunchApps,
    loading,
    loadError,
    retryLoad,
    saveState,
    saveError,
    addSection,
    renameSection,
    deleteSection,
    moveSection,
    addApp,
    addQuickLaunchApp,
    addToQuickLaunch,
    removeFromQuickLaunch,
    moveQuickLaunch,
    updateApp,
    deleteApp,
    moveApp,
    saveAppForLater,
    addVaultTool,
    updateVaultTool,
    restoreVaultTool,
    importLegacy
  } = useSections();
  const expenses = useExpenses({
    workspaceId: workspace?.id || null,
    userId: session?.user?.id || null
  });
  const [view, setView] = useState('apps');
  const [vaultOpen, setVaultOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [dialog, setDialog] = useState(null);
  const closeDialog = () => setDialog((current) => {
    if (current?.returnToQuickLaunch) return { type: 'quickLaunch' };
    if (current?.type === 'addVaultGroup') return { type: 'moveVaultTool', app: current.app };
    return null;
  });
  const quickSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const finishQuickDrag = ({ active, over }) => {
    const activeId = active.data.current?.appId;
    const overId = over?.data.current?.appId;
    if (activeId && overId && activeId !== overId) moveQuickLaunch(activeId, overId);
  };

  if (!authReady) {
    return <main className="auth-screen" aria-live="polite">Loading Tefolio…</main>;
  }
  if (!session) {
    return <LoginScreen onSignIn={signIn} />;
  }

  const loaded = Boolean(workspace) && !loading && !loadError;
  const hasSections = sections.length > 0;
  const appsView = view === 'apps';

  return (
    <main className="dashboard-shell">
      <header className="topbar">
        <div className="topbar-left">
          <a
            className="brand"
            href="#home"
            aria-label="Tefolio home"
            onClick={() => {
              setView('apps');
              setVaultOpen(false);
              setEditMode(false);
            }}
          >
            <span>Tefolio</span>
          </a>
          <Segmented
            label="View"
            className="view-switcher"
            options={VIEWS}
            value={view}
            onChange={setView}
          />
        </div>
        <DndContext sensors={quickSensors} onDragEnd={finishQuickDrag}>
          <QuickLaunch
            apps={quickLaunchApps}
            editMode={editMode && appsView && !vaultOpen}
            onAdd={() => setDialog({ type: 'quickLaunch' })}
            onEdit={(app) => setDialog({ type: 'editQuickLaunch', app })}
            onRemove={removeFromQuickLaunch}
            onReorder={moveQuickLaunch}
          />
        </DndContext>
        <div className="topbar-actions">
          <AccountMenu email={session.user.email || ''} onSignOut={signOut} />
          {appsView && !vaultOpen && (
            <button
              type="button"
              className="btn"
              aria-pressed={editMode}
              onClick={() => setEditMode((value) => !value)}
            >
              {editMode ? 'Done' : 'Edit'}
            </button>
          )}
        </div>
      </header>

      <div className="workspace" id="home">
        {!appsView && (
          <BurnRateView
            workspaceReady={Boolean(workspace)}
            workspaceError={loadError}
            onRetryWorkspace={retryLoad}
            api={expenses}
          />
        )}
        {appsView && (
          <div className="apps-toolbar">
            {vaultOpen ? (
              <button type="button" className="btn" onClick={() => setVaultOpen(false)}>
                Back to Apps
              </button>
            ) : (
              <button type="button" className="btn" onClick={() => setVaultOpen(true)}>
                Vault
              </button>
            )}
          </div>
        )}
        {appsView && (loading || (!loadError && saveState !== 'idle')) && (
          <p className={`data-status${saveState === 'error' || saveState === 'refreshError' ? ' is-error' : ''}`} role="status">
            {loading ? 'Loading workspace…'
              : saveState === 'saving' ? 'Saving…'
                : saveState === 'error' ? `Not saved: ${saveError}`
                  : saveState === 'refreshError' ? `Saved, but workspace refresh failed: ${saveError}`
                    : 'Saved'}
            {saveState === 'refreshError' && (
              <button type="button" className="text-btn" onClick={retryLoad}>Try again</button>
            )}
          </p>
        )}
        {appsView && loadError && !loading && (
          <WorkspaceState
            role="alert"
            title="Couldn't load your workspace."
            actionLabel="Try again"
            onAction={retryLoad}
          />
        )}
        {appsView && vaultOpen && loaded && (
          <VaultView
            apps={vaultApps}
            onAdd={() => setDialog({ type: 'addVaultTool' })}
            onEdit={(app) => setDialog({ type: 'editVaultTool', app })}
            onMove={(app) => setDialog({ type: 'moveVaultTool', app })}
            onDelete={(app) => setDialog({ type: 'deleteVaultTool', app })}
          />
        )}
        {appsView && !vaultOpen && loaded && !hasSections && (
          <WorkspaceState
            title="Create your first group"
            text="Add a group to start organizing your apps."
            actionLabel="Create group"
            onAction={() => setDialog({ type: 'add' })}
          />
        )}
        {appsView && !vaultOpen && loaded && hasSections && editMode && (
          <div className="edit-bar">
            <button type="button" className="btn" onClick={() => setDialog({ type: 'add' })}>
              + Add Group
            </button>
          </div>
        )}
        {appsView && !vaultOpen && loaded && hasSections && (
          <SortableDashboard sections={sections} moveSection={moveSection} moveApp={moveApp}>
            <motion.div
              className="section-grid"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              {sections.map((section, index) => (
                <DashboardSection
                  key={section.id}
                  section={section}
                  index={index}
                  editMode={editMode}
                  onRename={(s) => setDialog({ type: 'rename', section: s })}
                  onDelete={(s) => setDialog({ type: 'delete', section: s })}
                  onAddApp={(s) => setDialog({ type: 'addApp', section: s })}
                  onEditApp={(app, s) => setDialog({ type: 'editApp', app, section: s })}
                  onMoveApp={(app, s) => setDialog({ type: 'moveApp', app, section: s })}
                  onDeleteApp={(app) => setDialog({ type: 'deleteApp', app })}
                  onSaveForLater={(app) => setDialog({ type: 'confirmVaultMove', app })}
                />
              ))}
            </motion.div>
          </SortableDashboard>
        )}
      </div>

      {dialog?.type === 'quickLaunch' && (
        <QuickLaunchPickerDialog
          apps={allApps}
          quickLaunchIds={quickLaunchIds}
          onAdd={addToQuickLaunch}
          onCreate={addQuickLaunchApp}
          onDelete={(app) => setDialog({ type: 'deleteApp', app, returnToQuickLaunch: true })}
          onSaveForLater={(app) => setDialog({
            type: 'confirmVaultMove', app, returnToQuickLaunch: true
          })}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'add' && (
        <SectionNameDialog
          title="Add group"
          submitLabel="Add"
          onSubmit={addSection}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'addVaultGroup' && (
        <SectionNameDialog
          title="Add group"
          submitLabel="Add"
          onSubmit={addSection}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'rename' && (
        <SectionNameDialog
          title="Rename group"
          submitLabel="Save"
          initialName={dialog.section.title}
          onSubmit={(name) => renameSection(dialog.section.id, name)}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'delete' && (
        <ConfirmDeleteDialog
          section={dialog.section}
          quickLaunchIds={quickLaunchIds}
          onConfirm={deleteSection}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'addApp' && (
        <AppDialog
          title="Add application"
          submitLabel="Add"
          sections={sections}
          sectionId={dialog.section.id}
          onSubmit={(values, target) => addApp(target, values)}
          onClose={closeDialog}
        />
      )}
      {(dialog?.type === 'editApp' || dialog?.type === 'editQuickLaunch') && (
        <AppDialog
          title="Edit application"
          submitLabel="Save"
          app={dialog.app}
          sections={sections}
          onSubmit={(values) => updateApp(dialog.app.id, values)}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'moveApp' && (
        <MoveAppDialog
          app={dialog.app}
          sections={sections}
          currentSectionId={dialog.section.id}
          onSubmit={(targetId) => moveApp(dialog.app.id, targetId)}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'deleteApp' && (
        <ConfirmAppDeleteDialog
          app={dialog.app}
          onConfirm={deleteApp}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'addVaultTool' && (
        <VaultToolDialog
          title="Add tool"
          submitLabel="Add tool"
          onSubmit={addVaultTool}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'editVaultTool' && (
        <VaultToolDialog
          title="Edit tool"
          submitLabel="Save"
          app={dialog.app}
          onSubmit={(values) => updateVaultTool(dialog.app.id, values)}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'confirmVaultMove' && (
        <ConfirmVaultMoveDialog
          app={dialog.app}
          pinned={quickLaunchIds.includes(dialog.app.id)}
          onConfirm={saveAppForLater}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'moveVaultTool' && (
        <MoveVaultToolDialog
          app={dialog.app}
          sections={sections}
          onSubmit={(sectionId) => restoreVaultTool(dialog.app.id, sectionId)}
          onCreateGroup={() => setDialog({ type: 'addVaultGroup', app: dialog.app })}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === 'deleteVaultTool' && (
        <DeleteVaultToolDialog
          app={dialog.app}
          onConfirm={deleteApp}
          onClose={closeDialog}
        />
      )}
    </main>
  );
}

export default App;